import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bell, Send, Users, User, MessageSquare } from 'lucide-react';
import { API } from '../App';

export default function NotificationManagement() {
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    recipient_type: 'all',
    recipient_ids: [],
    title: '',
    message: '',
    template_type: 'custom'
  });
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('send'); // 'send' or 'history'
  const [sentHistory, setSentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    loadTemplates();
    loadUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      setCurrentPage(1);
      loadSentHistory(1);
    }
  }, [activeTab]);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notification-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch (error) {
      console.error('載入模板失敗:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('載入用戶失敗:', error);
    }
  };

  const loadSentHistory = async (page = 1) => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/sent-history?page=${page}&page_size=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSentHistory(res.data.data);
      setTotalPages(res.data.total_pages);
      setTotalRecords(res.data.total);
      setCurrentPage(page);
    } catch (error) {
      console.error('載入發送歷史失敗:', error);
      toast.error('載入發送歷史失敗');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTemplateSelect = (templateType) => {
    const template = templates.find(t => t.template_type === templateType);
    if (template) {
      setFormData({
        ...formData,
        title: template.title,
        message: template.message,
        template_type: templateType
      });
    } else if (templateType === 'custom') {
      setFormData({
        ...formData,
        title: '',
        message: '',
        template_type: 'custom'
      });
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.message) {
      toast.error('請填寫標題和訊息');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const sendData = new FormData();
      sendData.append('recipient_type', formData.recipient_type);
      sendData.append('title', formData.title);
      sendData.append('message', formData.message);
      sendData.append('template_type', formData.template_type);
      
      if (formData.recipient_type === 'specific' && formData.recipient_ids.length > 0) {
        sendData.append('recipient_ids', JSON.stringify(formData.recipient_ids));
      }
      
      const res = await axios.post(`${API}/notifications/send`, sendData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`✅ 通知已發送給 ${res.data.count} 位用戶`);
      setDialogOpen(false);
      setFormData({
        recipient_type: 'all',
        recipient_ids: [],
        title: '',
        message: '',
        template_type: 'custom'
      });
      
      // Refresh history if on history tab
      if (activeTab === 'history') {
        loadSentHistory(currentPage);
      }
    } catch (error) {
      toast.error('發送失敗: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  const recipientTypeOptions = [
    { value: 'all', label: '所有用戶', icon: Users, color: 'bg-blue-100 text-blue-700' },
    { value: 'student', label: '所有學生', icon: User, color: 'bg-green-100 text-green-700' },
    { value: 'parent', label: '所有家長', icon: Users, color: 'bg-purple-100 text-purple-700' },
    { value: 'coach', label: '所有教練', icon: User, color: 'bg-orange-100 text-orange-700' },
    { value: 'specific', label: '特定用戶', icon: User, color: 'bg-gray-100 text-gray-700' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">推送通知管理</h2>
          <p className="text-gray-600 text-sm mt-1">向用戶發送通知提醒</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-500 hover:bg-blue-600">
              <Send className="mr-2" size={16} />
              發送通知
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>發送推送通知</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">接收對象</label>
                <Select value={formData.recipient_type} onValueChange={(value) => setFormData({...formData, recipient_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {recipientTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.recipient_type === 'specific' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">選擇用戶</label>
                  <Select 
                    value={formData.recipient_ids[0] || ''} 
                    onValueChange={(value) => setFormData({...formData, recipient_ids: [value]})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="選擇用戶" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.username} ({user.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium mb-2 block">通知模板</label>
                <Select value={formData.template_type} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">自訂訊息</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.template_id} value={template.template_type}>
                        {template.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">標題</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="例如：今日任務提醒"
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">訊息內容</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="輸入通知內容..."
                  rows={4}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={sending}>
                {sending ? '發送中...' : '發送通知'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('send')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'send'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Send className="inline mr-2" size={16} />
              發送通知
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Bell className="inline mr-2" size={16} />
              發送歷史
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'history' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">通知發送歷史（最近3天）</h3>
                <span className="text-sm text-gray-500">共 {totalRecords} 筆記錄</span>
              </div>
              {loadingHistory ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <p className="mt-2 text-gray-600">載入中...</p>
                </div>
              ) : sentHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>暫無發送記錄（最近3天）</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {sentHistory.map((notification, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Bell size={16} className="text-blue-500" />
                            <h4 className="font-semibold text-gray-800">{notification.title}</h4>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(notification.sent_at).toLocaleString('zh-TW')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>接收對象: {notification.recipient_type_display}</span>
                          <span>發送數量: {notification.recipient_count} 人</span>
                          {notification.template_type !== 'custom' && (
                            <span>模板: {notification.template_type}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadSentHistory(currentPage - 1)}
                        disabled={currentPage === 1 || loadingHistory}
                      >
                        上一頁
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={page === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => loadSentHistory(page)}
                            disabled={loadingHistory}
                            className={page === currentPage ? "bg-blue-500 hover:bg-blue-600" : ""}
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadSentHistory(currentPage + 1)}
                        disabled={currentPage === totalPages || loadingHistory}
                      >
                        下一頁
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {activeTab === 'send' && (
        <>
          {/* Quick Send Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {recipientTypeOptions.map((option) => {
          const Icon = option.icon;
          return (
            <div key={option.value} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className={`w-12 h-12 rounded-full ${option.color} flex items-center justify-center mb-4`}>
                <Icon size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{option.label}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {option.value === 'all' && '向機構內所有用戶發送通知'}
                {option.value === 'student' && '向所有學生發送訓練提醒'}
                {option.value === 'parent' && '向所有家長發送進度通知'}
                {option.value === 'coach' && '向所有教練發送管理通知'}
                {option.value === 'specific' && '向特定用戶發送個人通知'}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  setFormData({...formData, recipient_type: option.value});
                  setDialogOpen(true);
                }}
              >
                <Send size={14} className="mr-2" />
                發送
              </Button>
            </div>
          );
        })}
      </div>

      {/* Template Preview */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <MessageSquare size={20} />
          預設通知模板
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <div key={template.template_id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-blue-500" />
                  <h4 className="font-semibold text-gray-800">{template.title}</h4>
                </div>
              </div>
              <p className="text-sm text-gray-600">{template.message}</p>
            </div>
          ))}
        </div>
      </div>
        </>
      )}
    </div>
  );
}
